/* eslint-disable css-modules/no-undef-class */
import { m } from 'framer-motion';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import Video from '@/components/atoms/video/video';
import variables from '@/components/modules/card-banner/card-banner.variables';
import Button from '@/components/molecules/button/button';
import InspirationalSearchCard from '@/components/organisms/inspirational-search-card/inspirational-search-card';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import breakpoints from '@/styles/modules/breakpoints.module.scss';
import styles from './card-banner.module.scss';

export interface CardBannerProps extends ModuleWrapperProps {
  /** The steps to show in the flow */
  cards?: Content.Components['CardBannerModuleDto']['cards'];
  /** The text to show as a headline */
  headline?: Content.Components['CardBannerModuleDto']['headline'];
  /** The link of the result page */
  link?: Content.Components['CardBannerModuleDto']['link'];
  /** Indicate whether to show the video background */
  showVideo?: Content.Components['CardBannerModuleDto']['showVideo'];
  /** The text to show as a sub headline */
  subHeadline?: Content.Components['CardBannerModuleDto']['subHeadline'];
}

const CardBanner = ({
  cards = [],
  headline,
  link,
  showVideo = false,
  subHeadline,
  ...rest
}: CardBannerProps) => {
  const isTablet = useWindowSize('tablet');

  return (
    <ModuleWrapper {...rest} className={styles.component}>
      <Container>
        <div className={styles.step}>
          <div className={styles.stepContent}>
            {variables.showBackground && (
              <div className={styles.stepVideoWrapper}>
                {showVideo ? (
                  <Video
                    autoPlay
                    className={styles.stepVideo}
                    isPlaying
                    src="/assets/modules/inspirational-search-flow/background.webm"
                    type="video/webm"
                  />
                ) : (
                  <Image
                    alt=""
                    className={styles.stepImage}
                    height={800}
                    sizes={`(min-width: ${parseInt(breakpoints.desktop)}px) 1344px, 100vw`}
                    src="/assets/modules/inspirational-search-flow/background.webp"
                    width={1344}
                  />
                )}
              </div>
            )}

            <Container className={styles.cardsContainer}>
              <m.div
                className={styles.cards}
                initial="hidden"
                variants={{
                  hidden: { opacity: 1 },
                  show: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.25,
                    },
                  },
                }}
                viewport={{ amount: 0.5, once: true }}
                whileInView="show"
              >
                {cards.map((card, cardIndex) => (
                  <m.span
                    key={cardIndex}
                    className={styles.card}
                    variants={
                      cardIndex === 0
                        ? {
                            hidden: {
                              opacity: 0,
                              transform: isTablet
                                ? 'rotate(10deg) translate(-10%, -40%)'
                                : 'rotate(0deg) translate(0%, -50%)',
                            },
                            show: {
                              opacity: 1,
                              transform: isTablet
                                ? 'rotate(-10deg) translate(40%, 10%)'
                                : 'rotate(-20deg) translate(50%, 0%)',
                              transition: {
                                default: { type: 'spring' },
                                opacity: { ease: 'linear' },
                              },
                            },
                          }
                        : cardIndex === 1
                          ? {
                              hidden: {
                                opacity: 0,
                                transform: isTablet
                                  ? 'rotate(5deg) translate(-30%, -60%)'
                                  : 'rotate(0deg) translate(0%, -50%)',
                              },
                              show: {
                                opacity: 1,
                                transform: isTablet
                                  ? 'rotate(-5deg) translate(20%, -10%)'
                                  : 'rotate(0deg) translate(0%, 0%)',
                                transition: {
                                  default: { type: 'spring' },
                                  opacity: { ease: 'linear' },
                                },
                              },
                            }
                          : cardIndex === 2
                            ? {
                                hidden: {
                                  opacity: 0,
                                  transform: isTablet
                                    ? 'rotate(-5deg) translate(30%, -60%)'
                                    : 'rotate(0deg) translate(0%, -50%)',
                                },
                                show: {
                                  opacity: 1,
                                  transform: isTablet
                                    ? 'rotate(5deg) translate(-20%, -10%)'
                                    : 'rotate(20deg) translate(-50%, 0%)',
                                  transition: {
                                    default: { type: 'spring' },
                                    opacity: { ease: 'linear' },
                                  },
                                },
                              }
                            : {
                                hidden: {
                                  opacity: 0,
                                  transform: isTablet
                                    ? 'rotate(-10deg) translate(10%, -40%)'
                                    : 'rotate(0deg) translate(0%, 0%)',
                                },
                                show: {
                                  opacity: 1,
                                  transform: isTablet
                                    ? 'rotate(10deg) translate(-40%, 10%)'
                                    : 'rotate(0deg) translate(0%, 0%)',
                                  transition: {
                                    default: { type: 'spring' },
                                    opacity: { ease: 'linear' },
                                  },
                                },
                              }
                    }
                  >
                    <InspirationalSearchCard
                      alwaysShowImage
                      headline={card.imageCaption}
                      image={card.image}
                    />
                  </m.span>
                ))}
              </m.div>
            </Container>

            <Container className={styles.contentContainer}>
              {(headline || subHeadline) && (
                <div className={styles.headlineWrapper}>
                  {headline && (
                    <Typography
                      className={styles.headline}
                      tagStyle="displayMedium"
                    >
                      {headline}
                    </Typography>
                  )}

                  {subHeadline && (
                    <Typography tagStyle="bodyLarge">{subHeadline}</Typography>
                  )}
                </div>
              )}

              {link && (
                <Button target={link.target} text={link.text} url={link.url} />
              )}
            </Container>
          </div>
        </div>
      </Container>
    </ModuleWrapper>
  );
};

CardBanner.displayName = 'CardBanner';

export default CardBanner;
